/**
 * Mycelium Connector Registry
 *
 * Centralized registry for all Mycelium connectors. Replaces hardcoded imports
 * in mycelium-init.ts with a dynamic, discoverable connector management system.
 *
 * Features:
 * - Dynamic registration (connectors self-register via `register()`)
 * - Transport-aware initialization (skips PGMQ-only connectors in dev)
 * - Ordered initialization with dependency awareness
 * - Health reporting and connector discovery
 * - Graceful reverse-order disposal
 *
 * @module mycelium-connector-registry
 * @since 2026-04-07
 * @see docs/protocols/mycelium/CONNECTOR_FRAMEWORK.md
 */

import type { IMyceliumConnector, ConnectorMetadata } from "./mycelium-connector.interface";
import { getMyceliumTransportType } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";

// ============================================================================
// TYPES
// ============================================================================

export interface ConnectorRegistryStats {
  /** Total connectors registered */
  registered: number;
  /** Currently initialized (active) connectors */
  initialized: number;
  /** Connectors skipped (e.g. PGMQ-only in dev mode) */
  skipped: number;
  /** Connector IDs by status */
  connectors: {
    id: string;
    name: string;
    phase: number;
    status: "active" | "registered" | "skipped" | "failed";
    subscribedDomains: readonly string[];
    publishedDomains: readonly string[];
  }[];
}

// ============================================================================
// REGISTRY
// ============================================================================

export class ConnectorRegistry {
  /** Registered connectors in insertion order */
  private connectors = new Map<string, IMyceliumConnector>();

  /** Connectors that were initialized (for reverse-order disposal) */
  private initializationOrder: string[] = [];

  /** Connectors that failed initialization */
  private failedConnectors = new Set<string>();

  /** Connectors that were skipped (transport mismatch) */
  private skippedConnectors = new Set<string>();

  /**
   * Register a connector with the registry.
   * Does NOT initialize the connector — call `initializeAll()` after registration.
   * Silently skips duplicates with a warning log.
   */
  register(connector: IMyceliumConnector): void {
    const { id, name } = connector.metadata;

    if (this.connectors.has(id)) {
      // Expected on re-init (edge isolates, hot reloads) — not warn-worthy.
      // warn() here was emitting ~8 system_warning_reported Mycelium events
      // per isolate boot, which flooded the queue (tenant-less events were
      // schema-rejected → DLQ).
      logger.debug(`ConnectorRegistry: connector '${id}' already registered, skipping duplicate`);
      return;
    }

    this.connectors.set(id, connector);
    logger.debug(`📝 ConnectorRegistry: registered '${name}' (${id})`);
  }

  /**
   * Initialize all registered connectors in registration order.
   * Skips connectors that require PGMQ when the active transport is not PGMQ.
   * Continues past failures — records them for health reporting.
   */
  async initializeAll(): Promise<void> {
    const transportType = getMyceliumTransportType();
    logger.info(`🍄 ConnectorRegistry: initializing ${this.connectors.size} connectors (transport: ${transportType})`);

    for (const [id, connector] of this.connectors) {
      const { name, requiresPgmq } = connector.metadata;

      // Skip server-only connectors in browser environments
      if (connector.metadata.serverOnly && typeof window !== "undefined") {
        this.skippedConnectors.add(id);
        logger.debug(`⏭️ ConnectorRegistry: skipped '${name}' (server-only connector, running in browser)`);
        continue;
      }

      // Skip PGMQ-only connectors when not using PGMQ transport
      if (requiresPgmq && transportType !== "pgmq") {
        this.skippedConnectors.add(id);
        logger.debug(`⏭️ ConnectorRegistry: skipped '${name}' (requires PGMQ, active transport: ${transportType})`);
        continue;
      }

      try {
        await connector.initialize();
        this.initializationOrder.push(id);
      } catch (error) {
        this.failedConnectors.add(id);
        logger.error(`❌ ConnectorRegistry: failed to initialize '${name}'`, {
          error: error instanceof Error ? error.message : String(error),
        });
        // Continue — don't let one connector failure block the rest
      }
    }

    const active = this.initializationOrder.length;
    const skipped = this.skippedConnectors.size;
    const failed = this.failedConnectors.size;

    logger.info(`✅ ConnectorRegistry: ${active} active, ${skipped} skipped, ${failed} failed`);
  }

  /**
   * Dispose all initialized connectors in reverse initialization order.
   * Continues past errors to ensure all connectors get a chance to clean up.
   */
  async disposeAll(): Promise<void> {
    logger.info(`🧹 ConnectorRegistry: disposing ${this.initializationOrder.length} connectors`);

    // Dispose in reverse initialization order
    for (let i = this.initializationOrder.length - 1; i >= 0; i--) {
      const id = this.initializationOrder[i];
      const connector = this.connectors.get(id);
      if (!connector) continue;

      try {
        await connector.dispose();
        logger.debug(`✅ ConnectorRegistry: disposed '${connector.metadata.name}'`);
      } catch (error) {
        logger.error(`❌ ConnectorRegistry: error disposing '${connector.metadata.name}'`, {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    this.initializationOrder = [];
    this.failedConnectors.clear();
    this.skippedConnectors.clear();
    logger.info("✅ ConnectorRegistry: all connectors disposed");
  }

  /**
   * Get a registered connector by ID.
   */
  get(id: string): IMyceliumConnector | undefined {
    return this.connectors.get(id);
  }

  /**
   * Check if a connector is registered.
   */
  has(id: string): boolean {
    return this.connectors.has(id);
  }

  /**
   * List all connector metadata for discovery/documentation.
   */
  listConnectors(): ConnectorMetadata[] {
    return Array.from(this.connectors.values()).map((c) => c.metadata);
  }

  /**
   * Get registry statistics for health/monitoring endpoints.
   */
  getStats(): ConnectorRegistryStats {
    const connectors = Array.from(this.connectors.entries()).map(([id, connector]) => {
      let status: "active" | "registered" | "skipped" | "failed";
      if (this.failedConnectors.has(id)) {
        status = "failed";
      } else if (this.skippedConnectors.has(id)) {
        status = "skipped";
      } else if (this.initializationOrder.includes(id)) {
        status = "active";
      } else {
        status = "registered";
      }

      return {
        id,
        name: connector.metadata.name,
        phase: connector.metadata.phase,
        status,
        subscribedDomains: connector.metadata.subscribedDomains,
        publishedDomains: connector.metadata.publishedDomains,
      };
    });

    return {
      registered: this.connectors.size,
      initialized: this.initializationOrder.length,
      skipped: this.skippedConnectors.size,
      connectors,
    };
  }

  /**
   * Get the count of active connectors.
   */
  get activeCount(): number {
    return this.initializationOrder.length;
  }

  /**
   * Clear all registrations (primarily for testing).
   */
  clear(): void {
    this.connectors.clear();
    this.initializationOrder = [];
    this.failedConnectors.clear();
    this.skippedConnectors.clear();
  }
}

// ============================================================================
// SINGLETON
// ============================================================================

/** Global connector registry instance */
export const connectorRegistry = new ConnectorRegistry();
